/**
 * Visual tokens for the coding-screen shell and its molecules. Single source of
 * truth for the shell's presentational chrome -- background/border/radius/shadow colors and the
 * fixed-geometry heights/rail-width every header bar, pane card, and strip renders at. Every
 * molecule/organism/template listed in the ticket imports its chrome from here instead of hand-coding
 * its own copy, so there is exactly one place to retune a value.
 *
 * Matches `docs/tickets/CDMX-18286/assets/empty-ui.png` (the approved empty-shell mockup) as closely
 * as a screenshot can be read by eye. EXACT VALUES ARE GUESSES pending a browser visual-comparison
 * pass -- this pass centralizes and re-tunes them from the earlier per-component scatter (where, before this change,
 * `PatientSummaryBar` hard-coded 56px and `DocumentTitleNav`/`ReviewTransactionsTabs` hard-coded 44px
 * -- the reverse of the mockup reading below), but a future browser pass may still adjust any of these
 * further. Nothing here is guaranteed pixel-final.
 *
 * Plain string/number literals only -- no functions, no theme references, no React types -- so this
 * file imports NOTHING and trivially satisfies the project_starburst isolation boundary guard, the
 * same "this file imports nothing" precedent `config/schema.ts` sets. STILL TRUE after the SECOND
 * styling pass below (rem units + biome CSS-variable colors): a `var(--color-x, #fallback)` reference
 * is a plain CSS function embedded in an ordinary string literal -- there is no import, no `url()`, no
 * `@import`/`@use`, nothing the AST-import boundary guard's file-reference walk (`__tests__/
 * boundary.test.ts`) recognizes as a module reference at all. It is exactly as inert to that guard as
 * the hex strings it replaces.
 *
 * WHY STRINGS, NOT BARE NUMBERS: every length below is a CSS string like `'2.5rem'` (formerly
 * `'40px'`), never a bare number. Chakra style props resolve a *number* against the theme's
 * `space`/`sizes` scale first (e.g. `theme.sizes[44]` is `'11rem'` -- 176px -- in the default theme,
 * not 44px), so a bare `44` passed to `h`/`w`/`minH` would silently resolve to the WRONG value. A
 * string like `'2.5rem'` does not match any scale key and is used literally -- the same protection a
 * px string had; only the unit changed. Every consumer in this folder already follows this rule (see
 * e.g. the pre-existing `DOCUMENT_TITLE_NAV_HEIGHT = '44px'` precedent); these tokens preserve it.
 *
 * SECOND STYLING PASS (dynamic units + theme color variables):
 *
 * 1. REM, NOT PX, FOR GEOMETRY: `TOPBAR_H`/`PATIENT_BAR_H`/`CASE_CONTEXT_BAR_H`/`STRIP_H`/`FOOTER_H`/
 *    `CARD_RADIUS`/`RAIL_WIDTH` are now `rem` strings (`px / 16`, this repo's -- and every browser's
 *    default -- root font-size), so the shell's fixed chrome scales with the user's root font-size
 *    (browser zoom / OS accessibility text-size settings) instead of staying pixel-locked. `GUTTER_PX`
 *    is the one deliberate holdout -- see its own doc comment below for why converting it is riskier
 *    than it looks and out of scope for this pass. Chakra's own spacing-SCALE tokens (`SHELL_PAD`/
 *    `REGION_GAP`) were already relative before this pass (Chakra maps a scale int to a `rem` value
 *    internally) and pane `sizePct` was already a `%` -- neither needed to change here. No component
 *    in this folder hard-codes an explicit Chakra `fontSize` in px today (checked across every
 *    molecule/organism/template) -- text sizing already comes from Chakra's theme `rem` scale, so
 *    there was nothing to convert on the font side.
 *
 * 2. THEME CSS VARIABLES FOR COLOR, WITH A HEX FALLBACK: every color token below is now
 *    `var(--color-<biome-token>, <original-hex>)` instead of a bare hex literal. `ui-components/dist/
 *    index.css` (loaded once, app-wide, outside this folder) defines these `--color-*` custom
 *    properties on `:root` from `@codametrix/design-tokens`'s biome palette
 *    (`packages/design-tokens/dist/css/biome/colors.css` in the ui-omnibus monorepo) -- so the real
 *    app picks up the platform's actual theme color the instant that stylesheet is present. The
 *    literal hex second argument is CSS's own `var()` fallback: whenever the custom property is
 *    UNDEFINED anywhere up the cascade (Storybook without the tokens stylesheet, a bare
 *    `<ChakraProvider>` unit test, jsdom -- none of which load `ui-components/dist/index.css`),
 *    `var(--color-x, #fallback)` evaluates to exactly `#fallback` -- the SAME value this token held
 *    before this pass, byte-for-byte. Nothing downstream (Chakra style props, raw inline `style`
 *    objects, template-literal `border` shorthands) parses or validates the string -- every consumer
 *    just forwards it verbatim to a CSS property -- so `var(...)` is a drop-in replacement for a hex
 *    literal everywhere a hex literal worked. Nearest-token choices (verified against the real
 *    `colors.css` values, not guessed) are documented per-token below; a token with no close-enough
 *    match in the current biome palette (`CARD_SHADOW`) is left as a plain literal instead of forcing
 *    a bad match -- also noted at that token.
 */

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

/** Page canvas -- light gray-lavender workspace background behind the whole shell. Nearest biome
 *  token: `--color-structural-300` (`#f2f8fa`), the Euclidean-nearest of the `structural`/`base`
 *  surface family to this token's original `#f3f3f7` (distance ~5.9 in per-channel-squared 0-255 RGB
 *  space -- imperceptibly close); kept in the `structural` family rather than the marginally closer
 *  `--color-chips-grey-50` (`#f0f2f4`, distance ~4.4) so every plain neutral surface in this file
 *  (`PAGE_BG`/`STRIP_BG`/`CARD_BG`/`BAR_BG`) draws from the same semantic "structural surface" token
 *  family. */
export const PAGE_BG = 'var(--color-structural-300, #f3f3f7)';

/** Top utility-bar strip's own background -- a faint lavender, distinct from the page canvas.
 *  `TopBarContainer` is `visibility:hidden` by default, which hides this
 *  background along with everything else in the container; the token still styles the strip
 *  correctly for whenever `header.topBar.visible` is `true`. Nearest biome token:
 *  `--color-chips-plum-50` (`#f3eaf2`) -- the palette's only lavender/plum family, and the choice
 *  that preserves this token's own doc comment above ("distinct from the page canvas"): the
 *  marginally closer `--color-chips-grey-50` (`#f0f2f4`, distance ~6.5 vs plum-50's ~7.8) is a
 *  neutral grey that would render this bar nearly identically to `PAGE_BG`'s own `structural-300`
 *  mapping, erasing the intentional lavender accent this bar is supposed to read as. */
export const TOPBAR_BG = 'var(--color-chips-plum-50, #efedf8)';

/** Patient-summary / case-context header bar background -- plain white. Nearest biome token:
 *  `--color-structural-0` (`#ffffff`) -- an EXACT match, not an approximation. */
export const BAR_BG = 'var(--color-structural-0, #ffffff)';

/** Bottom-border color shared by the patient-summary and case-context header bars. No biome token is
 *  a semantic "border" role -- the closest CANDIDATES by raw distance (`--color-chips-plum-50`,
 *  `--color-progress-300`) both shift this neutral grey hairline toward a visible pink/blue cast,
 *  which reads as a rendering mistake rather than an intentional accent on a 1px divider.
 *  `--color-chips-grey-50` (`#f0f2f4`) is the closest candidate that stays hue-neutral (grey in, grey
 *  out; distance ~15.4 vs plum-50's ~13.0) -- a deliberate hue-preservation-over-minimum-distance
 *  choice, applied consistently to `CARD_BORDER`/`STRIP_BORDER` below too, so all three hairlines
 *  share one genuinely neutral token instead of three different, individually "closest," hues. */
export const BAR_BORDER = 'var(--color-chips-grey-50, #e7e7ee)';

/** Pane card surface background (`DocumentationPane` / `ReviewTransactionsPane`), and the left
 *  rail's own background (the mockup reads the rail as white, matching the card body, not the
 *  off-white strip above it). Nearest biome token: `--color-structural-0` (`#ffffff`) -- an EXACT
 *  match, same as `BAR_BG` (both are plain white in the mockup). */
export const CARD_BG = 'var(--color-structural-0, #ffffff)';

/** Pane card border color. Same hue-neutral reasoning as `BAR_BORDER` above --
 *  `--color-chips-grey-50` (`#f0f2f4`) over the numerically-closer-but-hue-shifting
 *  `--color-chips-plum-50`. */
export const CARD_BORDER = 'var(--color-chips-grey-50, #e3e3ea)';

/** Pane card corner radius. `10px` -> `0.625rem` (÷16) -- see the file header's "REM, NOT PX" note. */
export const CARD_RADIUS = '0.625rem';

/** Pane card drop shadow. Left as an absolute-px `rgba(...)` box-shadow, NOT converted to a `rem`
 *  length or a theme variable: (1) the biome `colors.css` palette read for this pass has no
 *  elevation/shadow token, only flat `--color-*` hex values -- a multi-part
 *  `offset-x offset-y blur-radius color` box-shadow isn't representable by a single color variable;
 *  (2) shadow blur/offset is a fine-detail decorative cue, not a layout dimension that needs to track
 *  root font-size/zoom the way a bar height or rail width does. */
export const CARD_SHADOW = '0 1px 3px rgba(16, 16, 40, 0.06)';

/** Off-white background shared by every in-card "quiet chrome band": the pane header strips
 *  (`DocumentTitleNav` / `ReviewTransactionsTabs`), the pane footer strip (`PaneFooterSummary`), and
 *  the outer page footer (`PageFooter`) -- the mockup reads all four as the same light off-white
 *  family (the page footer's own reading is a hair lighter, ~#f7f7fa vs ~#f9f9fc, close enough to be
 *  one token pending the browser pass rather than adding a near-duplicate). Nearest biome token:
 *  `--color-structural-100` (`#f7fdff`), closest of the `structural` surface family to this token's
 *  original `#f9f9fc` (distance ~5.4). */
export const STRIP_BG = 'var(--color-structural-100, #f9f9fc)';

/** Border color paired with `STRIP_BG` above -- also used for the `DocumentationPane` left rail's
 *  border-right, which reads as the same quiet hairline in the mockup. Same hue-neutral
 *  `--color-chips-grey-50` choice as `BAR_BORDER`/`CARD_BORDER` above (and here it also happens to be
 *  the single closest biome token of ANY family, distance ~6.2, not just the closest neutral one). */
export const STRIP_BORDER = 'var(--color-chips-grey-50, #ededf2)';

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Top utility bar's reserved height (built but hidden by default -- see `TopBarContainer`).
 *  `40px` -> `2.5rem` (÷16) -- see the file header's "REM, NOT PX" note. */
export const TOPBAR_H = '2.5rem';

/** Patient-summary header bar's reserved height. `44px` -> `2.75rem` (÷16). */
export const PATIENT_BAR_H = '2.75rem';

/** Case-context header bar's reserved height. `40px` -> `2.5rem` (÷16). */
export const CASE_CONTEXT_BAR_H = '2.5rem';

/** Pane header-strip height, shared by `DocumentTitleNav` and `ReviewTransactionsTabs` so the
 *  left/right strips read as one visual row. `56px` -> `3.5rem` (÷16). */
export const STRIP_H = '3.5rem';

/** Pane footer-strip height (`PaneFooterSummary`), the outer page-footer height (`PageFooter`), and
 *  the two molecules that make up that outer footer's row (`ShortcutStatusBar`/`CaseIdentifier`) --
 *  all four share this one token instead of each hard-coding its own local
 *  `'40px'` copy, so a future retune propagates everywhere the footer row's height matters. `40px` ->
 *  `2.5rem` (÷16). */
export const FOOTER_H = '2.5rem';

/** `DocumentationPane` left rail's width, as a bare rem NUMBER -- kept LOCAL (not exported),
 *  since its only consumer is `RAIL_WIDTH` immediately below. Not exported
 *  for any numeric (non-CSS-string) consumer of its own -- there is none today -- because a bare
 *  number handed directly to a Chakra style prop is unsafe (see the file-level note above); if a
 *  genuine numeric consumer shows up later, re-export it then. Every actual consumer should use
 *  `RAIL_WIDTH` (below) for Chakra style props. `56px` -> `3.5rem` (÷16, same conversion as every
 *  other geometry token in this file). */
const RAIL_WIDTH_REM = 3.5;

/** `DocumentationPane` left rail's width, as a CSS length string -- what every Chakra width/border
 *  style prop should actually consume. */
export const RAIL_WIDTH = `${RAIL_WIDTH_REM}rem`;

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

/** `ResizableSplit`'s separator width in px -- the single source of truth for the gutter (was a
 *  locally hard-coded `DEFAULT_GUTTER_PX = 8` in `ResizableSplit.tsx`). A bare NUMBER, not a CSS
 *  string like the geometry tokens above: its only consumer, `ResizableSplitProps.gutterPx`, is
 *  typed `number` and interpolates it into a `calc(...)` template literal (`${gutterPx}px`), never
 *  hands it directly to a Chakra style prop -- so the "bare numbers resolve against Chakra's
 *  space/sizes scale" hazard documented at the top of this file does not apply to this token.
 *
 *  DELIBERATELY STILL PX, NOT REM (second styling pass known-scope exception): `ResizableSplit`'s
 *  drag/keyboard clamp math (`clampPct.ts`) measures the container via `getBoundingClientRect()`,
 *  which always returns real CSS PIXELS, and does exact pixel-precision arithmetic against
 *  `leftMinPx`/`rightMinPx`/this gutter (see `clampPct.ts`'s `CLAMP_BOUNDS_EPSILON` comment for how
 *  tightly that math is tuned -- down to float64 ULPs at exact-fit boundaries). Re-expressing the
 *  gutter as a `rem` token would only change what CSS *renders*; the clamp math would still need a
 *  live PIXEL number (either re-deriving one from `getComputedStyle(document.documentElement).
 *  fontSize` on every measurement, or accepting that the math and the paint silently disagree
 *  whenever the root font-size isn't exactly 16px). That is a real behavior change to
 *  `ResizableSplit`/`clampPct.ts` themselves, not a token-value edit, and risks the 40+ pixel-exact
 *  assertions in `ResizableSplit.test.tsx` (width-invariant, exact-fit-canonicalization, and
 *  ULP-tolerance cases) for a purely cosmetic win on an 8px separator most users will never zoom into
 *  individually. Out of scope for this pass -- left as a fixed pixel number on purpose; revisit only
 *  alongside a deliberate `ResizableSplit` change that re-derives the live px gutter from computed
 *  style. */
export const GUTTER_PX = 8;

/** Shell-level padding/gap rhythm. UNLIKE every token above, `SHELL_PAD`
 *  and `REGION_GAP` are DELIBERATELY bare Chakra spacing-SCALE numbers, not CSS px strings:
 *  `CodingScreenTemplate`'s root uses Chakra's `p`/`gap` style props for "breathing room" spacing,
 *  where a scale-relative value is the idiomatic Chakra choice -- `3` on Chakra's default scale is
 *  0.75rem (12px), squarely in `empty-ui.png`'s estimated ~10-12px rhythm -- as opposed to the
 *  FIXED, exact-pixel region heights above, which must stay raw px strings precisely to avoid that
 *  same scale-resolution hazard (see this file's header). Do not hand either of these to a prop that
 *  wants an exact px string. Already relative (Chakra resolves a scale int to a `rem` value
 *  internally) -- nothing to change here for the "dynamic units" pass above. */
/** Padding around the whole shell (`CodingScreenTemplate`'s root `p`) and a component's own edge
 *  padding (`PageFooter`'s `px`) -- both read as the same "outer padding" rhythm in `empty-ui.png`. */
export const SHELL_PAD = 3;
/** Gap between the shell's stacked regions -- header, content, footer (`CodingScreenTemplate`'s
 *  root `gap`). Same numeric value as `SHELL_PAD` today (both read the mockup's ~10-12px rhythm),
 *  kept as a DISTINCT token rather than reusing `SHELL_PAD` so padding and inter-region gap can be
 *  retuned independently later without one silently dragging the other along. */
export const REGION_GAP = 3;

// ---------------------------------------------------------------------------
// Warm neutral text + surfaces (shared)
// ---------------------------------------------------------------------------

/**
 * Warm-neutral palette shared by the patient popover (`PatientInfo`) and the Hold modal
 * (`HoldModal`, via `HOLD_COLORS`), and a ready home for `CaseIdentifier`'s duplicate hexes. biome's
 * neutrals are cool-teal-tinted (`--color-text-*` are teal #203d48/#717f96/#b0ccd6), so these WARM
 * near-black / grey / off-white values have NO faithful biome `--color-*` match -- they stay named
 * literals here (the same "no biome match -> literal" precedent `CARD_SHADOW` sets) so every molecule
 * that needs the design's warm ink draws from ONE token instead of re-inlining the same hex.
 */
/** Warm near-black -- primary text + popover-ink fills. */
export const INK = '#1a1818';
/** Warm dark-grey -- secondary text and emphasized row values. */
export const INK_SOFT = '#4a4751';
/** Warm mid-grey -- eyebrow / muted labels / secondary text (subtitle, close). */
export const INK_MUTED = '#7a7682';
/** Warm light-grey -- the faintest hints ("optional" / "select one" / placeholder), one step below
 *  INK_MUTED so hint text stays subordinate to the subtitle. */
export const INK_FAINT = '#a5a1ab';
/** Warm off-white -- raised popover surface fill (a hair warmer than the cool structural whites). */
export const SURFACE_WARM = '#f9f8fa';
/** Two-layer elevation for the raised patient popover -- heavier than `CARD_SHADOW`'s flat card lift.
 *  Same "biome has no shadow token" reasoning as `CARD_SHADOW`, so it stays a plain literal. */
export const POPOVER_SHADOW =
  '0 14px 36px -14px rgba(0, 0, 0, 0.4), 0 2px 8px -4px rgba(0, 0, 0, 0.18)';
/** Raised patient-popover corner. A dedicated 9px token (NOT the shell `CARD_RADIUS`) so the floating
 *  popover doesn't inherit future pane-card retunes, and an absolute px so it renders 9px on the app's
 *  14px root (a `rem` here would render ~12.5% small). */
export const POPOVER_RADIUS = '9px';
/** Raised patient-popover hairline border. biome `chips-grey-50`; the `#ececef` fallback preserves the
 *  popover's original border color in token-absent envs. Dedicated (not the shell `CARD_BORDER`) for
 *  the same decoupling reason as `POPOVER_RADIUS`. */
export const POPOVER_BORDER = 'var(--color-chips-grey-50, #e2e0e5)';

// ---------------------------------------------------------------------------
// Hold modal colors (CDMX-18412)
// ---------------------------------------------------------------------------

/**
 * Color tokens for the Hold modal (`molecules/HoldModal`). Grouped as one object (rather than the
 * flat `SHELL_*` consts above) so the component-specific palette stays namespaced -- consumers read
 * `HOLD_COLORS.<key>`.
 *
 * Biome CSS variables (`var(--color-<biome>, #hex)`) are used wherever the platform palette has a
 * match -- chips, alerts, hairlines all draw from biome now. The hex is CSS's own `var()` fallback
 * for environments without `ui-components/dist/index.css` (Storybook, jsdom unit tests). A few warm
 * accents (the gold pause icon, the warm off-white field fill, the near-black ink) have NO close
 * biome match -- biome's neutrals are cool-teal-tinted -- so they stay named literals to preserve
 * the approved mock. A `var(...)` reference is a plain CSS string: no import, inert to the
 * project_starburst boundary guard.
 */
export const HOLD_COLORS = {
  /** Hairline borders (inputs, dividers, pill outline). biome `chips-grey-50` (#f0f2f4). */
  border: 'var(--color-chips-grey-50, #ececef)',
  /** Close-button hover fill. biome `chips-grey-50` (#f0f2f4). */
  closeHoverBg: 'var(--color-chips-grey-50, #f0f0f2)',
  /** Pause-icon tile fill (pale cream). biome `chips-yellow-50` (#fff7d8). */
  iconBg: 'var(--color-chips-yellow-50, #fff4d6)',
  /** Selected reason/reminder chip fill + border. biome `chips-plum-500` -- the design system's own
   *  chip palette (replaces the former bespoke brand purple #8b1fc8). */
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  /** Validation error text + invalid border. biome `alert-500`. */
  error: 'var(--color-alert-500, #e50000)',
  /** Gold pause bars. Nearest biome yellow-500 (#e1b129) is far; kept literal. */
  iconBar: '#c99a3a',
  /** Warm off-white field/section fill. biome neutrals are cool -- no faithful match. */
  inputBg: '#fafaf7',
  /** Note textarea hover border (a touch darker than the resting hairline). */
  noteHoverBorder: '#e0e0e4',
  /** Warm dark-grey secondary text ("Logged as"). Shared `INK_SOFT`. */
  inkSoft: INK_SOFT,
  /** Near-black primary text + popover fill. Shared `INK`. */
  ink: INK,
  /** Warm mid-grey -- subtitle + close icon. Shared `INK_MUTED`. */
  inkMuted: INK_MUTED,
  /** Warm light-grey -- the faint field hints ("optional" / "select one"). Shared `INK_FAINT`. */
  inkFaint: INK_FAINT
} as const;

// ---------------------------------------------------------------------------
// Hold modal radii (CDMX-18412)
// ---------------------------------------------------------------------------

/**
 * Corner radii for the Hold modal + its trigger. Grouped like `HOLD_COLORS` so the modal's rounding
 * lives in one place instead of scattered raw literals -- and so the trigger icon tile and the modal's
 * inputs can't silently drift apart (they were `8px` vs `0.5rem` for the same corner before this).
 * ABSOLUTE PX, not rem: the app forces a 14px root (`src/styles/cmx.scss`), so a `rem` radius renders
 * ~12.5% smaller than its label; px keeps every corner at its documented size on any root.
 */
export const HOLD_RADIUS = {
  /** Modal outer corner. */
  modal: '14px',
  /** Header icon tile / raised inner tile. */
  tile: '9px',
  /** Inputs, note, hover popover, trigger icon -- the shared control corner. */
  control: '8px',
  /** Small square controls (close button). */
  small: '6px'
} as const;
